/*
 * File: /src/store/modules/postcardTemplates.js
 * Created Date: Wednesday September 24th 2025
 * Author: Nicolas Wegener, Roy Collins
 * -----
 * Last Modified: Wednesday, 24th September 2025 12:28:48 pm
 * Modified By: Nicolas Wegener (wegenern@gmail.com)
 * -----
 * Copyright (c) 2025 AdBase, Inc.
 */

import postcardTemplateService from '../../services/postcardTemplateServices';

const state = {
  postcardTemplates: [],
  loading: false,
  error: null,
};

const getters = {
  getPostcardTemplates: (state) => {
    const templates = state.postcardTemplates || [];
    // make a copy to avoid mutating state
    return [...templates].sort((a, b) => {
      const modifiedAt1 = new Date(a.modified_at).getTime();
      const modifiedAt2 = new Date(b.modified_at).getTime();
      return modifiedAt2 - modifiedAt1;
    });
  },
  getActivePostcardTemplates: (state, getters) => {
    return getters.getPostcardTemplates.filter((t) => !t.archived);
  },
  getArchivedPostcardTemplates: (state, getters) => {
    return getters.getPostcardTemplates.filter((t) => t.archived === true);
  },
  getPostcardTemplateById: (state) => (id) => {
    return state.postcardTemplates.find((template) => template.id === id);
  },
  isLoading: (state) => {
    return state.loading;
  },
};

const actions = {
  async fetchPostcardTemplates({ commit, state }) {
    if (state.postcardTemplates.length > 0 && !state.error) {
      return state.postcardTemplates;
    }

    commit('setLoading', true);
    commit('setError', null);

    try {
      const response = await postcardTemplateService.fetchTemplates(false);
      const templates = response.data || [];
      commit('setPostcardTemplates', templates);
      return templates;
    } catch (error) {
      console.error('Error fetching postcard templates:', error);
      commit('setError', error.message || 'Failed to fetch postcard templates');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async refreshPostcardTemplates({ commit }) {
    commit('setLoading', true);
    commit('setError', null);

    try {
      const response = await postcardTemplateService.fetchTemplates(false);
      const templates = response.data || [];
      commit('setPostcardTemplates', templates);
      return templates;
    } catch (error) {
      console.error('Error refreshing postcard templates:', error);
      commit(
        'setError',
        error.message || 'Failed to refresh postcard templates'
      );
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  clearPostcardTemplates({ commit }) {
    commit('setPostcardTemplates', []);
    commit('setError', null);
  },

  async addPostcardTemplate({ commit }, template) {
    try {
      commit('addTemplate', template);
      return template;
    } catch (error) {
      console.error('Error adding postcard template:', error);
      throw error;
    }
  },

  async updatePostcardTemplate({ commit }, { id, updates }) {
    try {
      commit('updateTemplate', { id, updates });
      return true;
    } catch (error) {
      console.error('Error updating postcard template:', error);
      throw error;
    }
  },

  async deletePostcardTemplate({ commit }, id) {
    try {
      commit('removeTemplate', id);
      return true;
    } catch (error) {
      console.error('Error deleting postcard template:', error);
      throw error;
    }
  },
};

const mutations = {
  setPostcardTemplates(state, templates) {
    state.postcardTemplates = templates;
  },

  setLoading(state, loading) {
    state.loading = loading;
  },

  setError(state, error) {
    state.error = error;
  },

  addTemplate(state, template) {
    if (!state.postcardTemplates.find((t) => t.id === template.id)) {
      state.postcardTemplates.push(template);
    }
  },

  updateTemplate(state, { id, updates }) {
    const index = state.postcardTemplates.findIndex((t) => t.id === id);
    if (index !== -1) {
      state.postcardTemplates[index] = {
        ...state.postcardTemplates[index],
        ...updates,
      };
    }
  },

  removeTemplate(state, id) {
    state.postcardTemplates = state.postcardTemplates.filter(
      (t) => t.id !== id
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
